/*
================================================================================
File: src/pages/SellerLeads/ContractForm.js (Refactored)
Description: Generates contracts for a seller lead with a structured and maintainable approach.
================================================================================
*/
import React, { useReducer, useMemo, useCallback, useState } from "react";
import Select from "react-tailwindcss-select";
import PageContent from "../../components/common/PageContent";
import { views } from "../../constants/views";
import moment from "moment";
import instance from "../../api/axios";
import JWT, { SupportedAlgorithms } from "expo-jwt";

// --- Helper Components ---

const RNSelect = ({ value, onChange, options, placeholder, className }) => {
  const selectedOption = useMemo(() => options.find(o => o.value === value) || null, [options, value]);
  return (
    <div className={className}>
      <Select
        options={options}
        onChange={(item) => onChange(item?.value)}
        value={selectedOption}
        placeholder={placeholder}
        classNames={{
    menuButton: ({ isDisabled }) =>
      `flex text-sm text-gray-700 border border-gray-300 rounded shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-indigo-500 focus:ring focus:ring-indigo-500/20 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200 ${
        isDisabled ? "bg-gray-200" : ""
      }`,
    menu: "absolute z-10 w-full bg-white shadow-lg border rounded py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200",
    listItem: ({ isSelected }) =>
      `block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded ${
        isSelected
          ? "text-white bg-indigo-500"
          : "text-gray-500 hover:bg-indigo-100 hover:text-indigo-500 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white"
      }`,
  }}

      />
    </div>
  );
};

const FormSection = ({ title, children }) => (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <span className="w-1 h-1 rounded-full bg-blue-600 dark:bg-blue-400"></span>
          {title}
        </h3>
        {children}
    </div>
);

const FormRow = ({ children }) => (
    <div className="flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0 mb-4">
        {children}
    </div>
);

const FormField = ({ label, children }) => (
    <div className="flex-1">
        <p className="mb-1 text-gray-700 text-sm dark:text-gray-200">{label}</p>
        {children}
    </div>
);

const TextInput = ({ value, onChange, placeholder, multiline = false, numeric = false }) => (
    <div className="w-full">
        {multiline ? (
            <textarea
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                rows="3"
            />
        ) : (
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                type={numeric ? "number" : "text"}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
            />
        )}
    </div>
);

// --- Reducer for State Management ---

const initialState = (user, leads) => ({
  // Meta
  PID: leads.pid,
  owner: user.app_id,
  device: "web",
  timestamp: moment().unix(),
  action: "download",
  type: "websignature",
  
  // Core
  buyer_name: user.co_name || "",
  seller_name: leads.property_owner_name || "",
  
  // Toggles
  trec: "0",
  seller_fin: "0",
  hoa: "0",
  buyer_terminate: "0",
  amendment: "0",
  loan_assum: "0",
  hydro_test: "0",
  terminate_appraisal: "0",
  lead_disclosure: "0",
  assign_agreement: "0",
  auth_rel_loan: "0",
  auth_rel_loan2: "0",
  sign: "0",

  // TREC 20-16
  legal_desc: leads.property_legal_desc || "",
  exclusions: "",
  real_lic: "",
  title_policy_party: "0",
  title_policy_checked: "0",
  title_policy_seller_buyer: "0",
  survey_party: "0",
  possession_closing: "0",
  possession_temporary: "0",
  option_period_days: user.default_period_option || "10",
  option_money: user.default_period_option_money || "100",
  earnest_money: user.earnest_money || "100",
  closing_date: moment().add(30, 'days').format('YYYY-MM-DD'),
  purchase_amount: "0",
  special_provisions: "Property is to be left broom swept clean and clear of all personal possessions.",
  title_company: user.preferr_title_name || "",
  title_address: user.preferr_title_address || "",

  // Seller Finance (26-7)
  fin_amount: "0",
  fin_percent: "0",
  fin_term_months: "0",
  fin_monthly_payment: "0",
  fin_date: "",

  // HOA (36-8)
  hoa_name: "",
  hoa_phone: "",
  hoa_deposit: "0",

  // Amendment (39-8)
  amend_sale_price_toggle: "0",
  amend_sale_price: "0",
  amend_para9_date_toggle: "0",
  amend_para9_date: "",
  amend_terminate_toggle: "0",
  amend_terminate_date: "",

  // Loan Assumption (41-2)
  loan_assum_monthly_payment: "",

  // Agreement to Assign
  end_buyer: "",
  assignment_fee: "",

  // Auth to Release Loan
  mortgage_lender: leads.loan_1_lender || "",
  mortgage_number: leads.loan_1_loan_id || "0",
  mortgage_lender2: leads.loan_2_lender || "",
  mortgage_number2: leads.loan_2_loan_id || "0",
  
  // Signature
  signatureData: null,
});

function formReducer(state, action) {
  switch (action.type) {
    case 'UPDATE_FIELD':
      return { ...state, [action.field]: action.value };
    default:
      throw new Error();
  }
}

export default function ContractForm({ onViewChange, user, leads, pulldata }) {
  const [state, dispatch] = useReducer(formReducer, initialState(user, leads));
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [lead, setLead] = useState(leads);

  const updateField = (field, value) => {
    dispatch({ type: 'UPDATE_FIELD', field, value });
  };
  
  const token = useMemo(() => {
    const data = { time: moment().unix(), id: user.app_id, email: user.app_email };
    return JWT.encode(data, user.app_api_key, { algorithm: SupportedAlgorithms.HS256 });
  }, [user]);

const pullLeads = useCallback(async (pid) => {
    try {
      const response = await instance.get(`/LoadHomes.php?u=${pid}&type=view`);
      setLead(response.data[0]);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError("Error pulling lead details: " + e.response.data.message);
        console.error("Error pulling lead details: ", e.response.data.message);
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
              setError(null);
    }
  }, []);

  const handleSubmit = async () => {
    setIsLoading(true);
    const formData = new FormData();
    for (const key in state) {
        formData.append(key, state[key]);
    }
    formData.append("token", token);

    try {
      const response = await instance.post("/contract_template.php", formData, {
          responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${leads.property_address}-Contract.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setError(null);
      await pullLeads(leads.pid);
      await pulldata(user?.app_id);
    } catch (e) {
      const errorMessage = e.response?.data?.message || e.message || "An unknown error occurred.";
      setError(`Error generating contract: ${errorMessage}`);
      console.error("Error generating contract:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const includeOptions = useMemo(() => [{ label: "Include", value: "1" }, { label: "Do Not Include", value: "0" }], []);
 // const yesNoOptions = useMemo(() => [{ label: "Yes", value: "1" }, { label: "No", value: "0" }], []);
  const checkedOptions = useMemo(() => [{ label: "Checked", value: "1" }, { label: "Not Checked", value: "0" }], []);
  const sellerBuyerOptions = useMemo(() => [{ label: "Seller", value: "1" }, { label: "Buyer", value: "0" }], []);

  return (
    <PageContent
      title="Seller Leads"
      subtitle="Generate Contract"
    >
      <div className="p-8 space-y-8">
        {/* Hero Banner */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white shadow-lg">
          <div className="flex items-center gap-4 mb-3">
            <div className="p-3 bg-white/20 rounded-lg">
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 4a2 2 0 012-2h6a1 1 0 01.707.293l2.828 2.829A1 1 0 0115 4v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 000 2h6a1 1 0 100-2H6z" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold">Contract Generation</h2>
              <p className="text-blue-100 text-sm">Property: {leads.property_address}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex gap-3">
          <button
            onClick={() => onViewChange(views.editLead, "sellerleads", { lead })}
            className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 dark:hover:bg-blue-500 transition-colors"
          >
            ← Back to Lead
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-lg p-4 text-red-700 dark:text-red-300">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Form Container */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          {/* Info Alert */}
          <div className="p-4 mb-6 text-sm text-blue-800 dark:text-blue-300 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zm-11-1a1 1 0 11-2 0 1 1 0 012 0zM8 9a1 1 0 000 2h6a1 1 0 100-2H8zm1-4a1 1 0 11-2 0 1 1 0 012 0zm5-1a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">Note:</span>
              <span>TREC forms are for Texas leads only.</span>
            </div>
          </div>
        
        <FormSection title="Parties">
            <FormRow>
                <FormField label="Buyer/Entity Name(s)">
                    <TextInput value={state.buyer_name} onChange={v => updateField('buyer_name', v)} placeholder="Buyer Name" />
                </FormField>
                <FormField label="Seller/Entity Name(s)">
                    <TextInput value={state.seller_name} onChange={v => updateField('seller_name', v)} placeholder="Seller Name" />
                </FormField>
            </FormRow>
        </FormSection>

        <FormSection title="TREC NO. 20-16 Home Contract">
            <RNSelect value={state.trec} onChange={v => updateField('trec', v)} options={includeOptions} placeholder="Include?" className="mb-4" />
            {state.trec === "1" && (
                <div className="space-y-4">
                    <FormField label="Legal Description"><TextInput value={state.legal_desc} onChange={v => updateField('legal_desc', v)} placeholder="Legal Description" multiline /></FormField>
                    <FormField label="Exclusions (Para 2, Sec D)"><TextInput value={state.exclusions} onChange={v => updateField('exclusions', v)} placeholder="Property Exclusions" multiline /></FormField>
                    <FormField label="Realtor License (Para 4)"><TextInput value={state.real_lic} onChange={v => updateField('real_lic', v)} placeholder="Realtor License" /></FormField>
                    <FormRow>
                        <FormField label="6. Title Policy (On/Seller Off/Buyer)"><RNSelect value={state.title_policy_party} onChange={v => updateField('title_policy_party', v)} options={sellerBuyerOptions} /></FormField>
                        <FormField label="6.A.8.ii. Title Policy"><RNSelect value={state.title_policy_checked} onChange={v => updateField('title_policy_checked', v)} options={checkedOptions} /></FormField>
                    </FormRow>
                    <FormRow>
                        <FormField label="6.A.8.ii. (On/Seller Off/Buyer)"><RNSelect value={state.title_policy_seller_buyer} onChange={v => updateField('title_policy_seller_buyer', v)} options={sellerBuyerOptions} /></FormField>
                        <FormField label="6.A.C.1. Survey (On/Seller Off/Buyer)"><RNSelect value={state.survey_party} onChange={v => updateField('survey_party', v)} options={sellerBuyerOptions} /></FormField>
                    </FormRow>
                    <FormRow>
                        <FormField label="10. Possession (Closing)"><RNSelect value={state.possession_closing} onChange={v => updateField('possession_closing', v)} options={checkedOptions} /></FormField>
                        <FormField label="10. Possession (Temporary)"><RNSelect value={state.possession_temporary} onChange={v => updateField('possession_temporary', v)} options={checkedOptions} /></FormField>
                    </FormRow>
                    <FormRow>
                        <FormField label="Option Period (Days)"><TextInput numeric value={state.option_period_days} onChange={v => updateField('option_period_days', v)} placeholder="10" /></FormField>
                        <FormField label="Option Money ($)"><TextInput numeric value={state.option_money} onChange={v => updateField('option_money', v)} placeholder="100" /></FormField>
                        <FormField label="Earnest Money ($)"><TextInput numeric value={state.earnest_money} onChange={v => updateField('earnest_money', v)} placeholder="100" /></FormField>
                    </FormRow>
                    <FormRow>
                        <FormField label="Closing Date (YYYY-MM-DD)"><TextInput value={state.closing_date} onChange={v => updateField('closing_date', v)} placeholder="YYYY-MM-DD" /></FormField>
                        <FormField label="Purchase Amount ($)"><TextInput numeric value={state.purchase_amount} onChange={v => updateField('purchase_amount', v)} placeholder="0" /></FormField>
                    </FormRow>
                    <FormField label="Special Provisions"><TextInput value={state.special_provisions} onChange={v => updateField('special_provisions', v)} placeholder="Special provisions" multiline /></FormField>
                    <FormRow>
                        <FormField label="Title Company"><TextInput value={state.title_company} onChange={v => updateField('title_company', v)} placeholder="Title Company Name" /></FormField>
                        <FormField label="Title Address"><TextInput value={state.title_address} onChange={v => updateField('title_address', v)} placeholder="Title Company Address" /></FormField>
                    </FormRow>
                </div>
            )}
        </FormSection>

        <FormSection title="TREC NO. 26-7 Seller Finance">
            <RNSelect value={state.seller_fin} onChange={v => updateField('seller_fin', v)} options={includeOptions} placeholder="Include?" className="mb-4" />
            {state.seller_fin === "1" && (
                <div className="space-y-4">
                    <FormRow>
                        <FormField label="Finance Amount ($)"><TextInput numeric value={state.fin_amount} onChange={v => updateField('fin_amount', v)} placeholder="0" /></FormField>
                        <FormField label="Percent (%)"><TextInput numeric value={state.fin_percent} onChange={v => updateField('fin_percent', v)} placeholder="0" /></FormField>
                    </FormRow>
                    <FormRow>
                        <FormField label="Term (Months)"><TextInput numeric value={state.fin_term_months} onChange={v => updateField('fin_term_months', v)} placeholder="0" /></FormField>
                        <FormField label="Monthly Payment ($)"><TextInput numeric value={state.fin_monthly_payment} onChange={v => updateField('fin_monthly_payment', v)} placeholder="0" /></FormField>
                    </FormRow>
                    <FormField label="Closing Date (YYYY-MM-DD)"><TextInput value={state.fin_date} onChange={v => updateField('fin_date', v)} placeholder="YYYY-MM-DD" /></FormField>
                </div>
            )}
        </FormSection>

        <FormSection title="TREC NO. 36-8 HOA Addendum">
            <RNSelect value={state.hoa} onChange={v => updateField('hoa', v)} options={includeOptions} placeholder="Include?" className="mb-4" />
            {state.hoa === "1" && (
                <div className="space-y-4">
                    <FormField label="HOA Name"><TextInput value={state.hoa_name} onChange={v => updateField('hoa_name', v)} placeholder="HOA Name" /></FormField>
                    <FormField label="HOA Phone"><TextInput value={state.hoa_phone} onChange={v => updateField('hoa_phone', v)} placeholder="HOA Phone" /></FormField>
                    <FormField label="HOA Deposit ($)"><TextInput numeric value={state.hoa_deposit} onChange={v => updateField('hoa_deposit', v)} placeholder="0" /></FormField>
                </div>
            )}
        </FormSection>

        <FormSection title="TREC NO. 38-5 Buyers Terminate">
            <RNSelect value={state.buyer_terminate} onChange={v => updateField('buyer_terminate', v)} options={includeOptions} placeholder="Include?" />
        </FormSection>

        <FormSection title="TREC NO. 39-8 Contract Amendment">
            <RNSelect value={state.amendment} onChange={v => updateField('amendment', v)} options={includeOptions} placeholder="Include?" className="mb-4" />
            {state.amendment === "1" && (
                <div className="space-y-4">
                    <FormField label="(1) Sale Price"><RNSelect value={state.amend_sale_price_toggle} onChange={v => updateField('amend_sale_price_toggle', v)} options={includeOptions} /></FormField>
                    {state.amend_sale_price_toggle === "1" && <FormField label="Sales Price ($)"><TextInput numeric value={state.amend_sale_price} onChange={v => updateField('amend_sale_price', v)} placeholder="0" /></FormField>}
                    <FormField label="(3) Paragraph 9 Date"><RNSelect value={state.amend_para9_date_toggle} onChange={v => updateField('amend_para9_date_toggle', v)} options={includeOptions} /></FormField>
                    {state.amend_para9_date_toggle === "1" && <FormField label="New Date (YYYY-MM-DD)"><TextInput value={state.amend_para9_date} onChange={v => updateField('amend_para9_date', v)} placeholder="YYYY-MM-DD" /></FormField>}
                    <FormField label="(6) Right to Terminate"><RNSelect value={state.amend_terminate_toggle} onChange={v => updateField('amend_terminate_toggle', v)} options={includeOptions} /></FormField>
                    {state.amend_terminate_toggle === "1" && <FormField label="New Date (YYYY-MM-DD)"><TextInput value={state.amend_terminate_date} onChange={v => updateField('amend_terminate_date', v)} placeholder="YYYY-MM-DD" /></FormField>}
                </div>
            )}
        </FormSection>

        <FormSection title="TREC NO. 41-2 Loan Assumption">
            <RNSelect value={state.loan_assum} onChange={v => updateField('loan_assum', v)} options={includeOptions} placeholder="Include?" className="mb-4" />
            {state.loan_assum === "1" && <FormField label="Monthly Payment ($)"><TextInput numeric value={state.loan_assum_monthly_payment} onChange={v => updateField('loan_assum_monthly_payment', v)} placeholder="0" /></FormField>}
        </FormSection>

        <FormSection title="TREC NO. 48-0 Hydrostatic Testing">
            <RNSelect value={state.hydro_test} onChange={v => updateField('hydro_test', v)} options={includeOptions} placeholder="Include?" />
        </FormSection>

        <FormSection title="TREC NO. 49-1 Terminate Lender Appraisal">
            <RNSelect value={state.terminate_appraisal} onChange={v => updateField('terminate_appraisal', v)} options={includeOptions} placeholder="Include?" />
        </FormSection>

        <FormSection title="TREC OP-L Lead Disclosure">
            <RNSelect value={state.lead_disclosure} onChange={v => updateField('lead_disclosure', v)} options={includeOptions} placeholder="Include?" />
        </FormSection>

        <FormSection title="Agreement to Assign">
            <RNSelect value={state.assign_agreement} onChange={v => updateField('assign_agreement', v)} options={includeOptions} placeholder="Include?" className="mb-4" />
            {state.assign_agreement === "1" && (
                <div className="space-y-4">
                    <FormField label="Buyer/Assignor"><TextInput value={state.end_buyer} onChange={v => updateField('end_buyer', v)} placeholder="Buyer/Assignor" /></FormField>
                    <FormField label="Assignment Fee ($)"><TextInput numeric value={state.assignment_fee} onChange={v => updateField('assignment_fee', v)} placeholder="0" /></FormField>
                </div>
            )}
        </FormSection>

        <FormSection title="Authorization to Release Loan">
            <RNSelect value={state.auth_rel_loan} onChange={v => updateField('auth_rel_loan', v)} options={includeOptions} placeholder="Include?" className="mb-4" />
            {state.auth_rel_loan === "1" && (
                <div className="space-y-4">
                    <FormField label="Loan Lender"><TextInput value={state.mortgage_lender} onChange={v => updateField('mortgage_lender', v)} placeholder="Loan Lender" /></FormField>
                    <FormField label="Loan Number"><TextInput value={state.mortgage_number} onChange={v => updateField('mortgage_number', v)} placeholder="Loan Number" /></FormField>
                </div>
            )}
        </FormSection>

        <FormSection title="Authorization to Release Loan 2">
            <RNSelect value={state.auth_rel_loan2} onChange={v => updateField('auth_rel_loan2', v)} options={includeOptions} placeholder="Include?" className="mb-4" />
            {state.auth_rel_loan2 === "1" && (
                <div className="space-y-4">
                    <FormField label="Loan Lender"><TextInput value={state.mortgage_lender2} onChange={v => updateField('mortgage_lender2', v)} placeholder="Loan Lender" /></FormField>
                    <FormField label="Loan Number"><TextInput value={state.mortgage_number2} onChange={v => updateField('mortgage_number2', v)} placeholder="Loan Number" /></FormField>
                </div>
            )}
        </FormSection>
{/* 
        <FormSection title="E-Sign Contract">
            <RNSelect value={state.sign} onChange={v => updateField('sign', v)} options={yesNoOptions} placeholder="Enable E-Sign?" />
        </FormSection> */}
        
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6 flex justify-end gap-3">
            <button
              onClick={() => onViewChange(views.editLead, "sellerleads", { lead })}
              className="px-6 py-2 rounded-lg font-semibold bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="px-6 py-2 rounded-lg font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? "Generating..." : "Generate Contract"}
            </button>
          </div>
        </div>
      </div>
    </PageContent>
  );
}
